'use client';

import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';

const RATINGS = [
  { emoji: '😞', label: 'Terrible' },
  { emoji: '🙁', label: 'Bad' },
  { emoji: '😐', label: 'Okay' },
  { emoji: '🙂', label: 'Good' },
  { emoji: '😄', label: 'Great' },
];

type EmojiRatingFieldProps<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  required?: boolean;
  control: Control<T>;
  error?: string;
  colSpan?: 1 | 2;
};

export function EmojiRatingField<T extends FieldValues>({
  name,
  label,
  required,
  control,
  error,
}: EmojiRatingFieldProps<T>) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-royal-500 text-xs font-semibold">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <div className="mx-auto flex items-center gap-4">
              {RATINGS.map((r, i) => {
                const selected = field.value === i;
                return (
                  <button
                    key={i}
                    type="button"
                    aria-label={r.label}
                    onClick={() => field.onChange(i)}
                    className={`cursor-pointer text-3xl transition-all duration-150 focus:outline-none ${
                      selected
                        ? 'scale-110 grayscale-0'
                        : 'grayscale hover:scale-110 hover:grayscale-0'
                    }`}
                  >
                    {r.emoji}
                  </button>
                );
              })}
            </div>
            {field.value !== null && field.value !== undefined && (
              <p className="text-royal-500 text-center text-xs font-bold">
                {RATINGS[field.value as number]?.label}
              </p>
            )}
            <hr className="border-gray-200" />
          </div>
        )}
      />
      {error && (
        <span className="mt-0.5 text-[10px] text-red-500">{error}</span>
      )}
    </div>
  );
}
